<template>
	<div class="content-page content-page--vendor-details">
		<ActionSetNav
			v-if="vendorData"
			:name="vendorName"
			:display="!loading"
			:actions="actions"
		/>
		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">3P Vendor Details</h2>
			</div>
		</div>

		<Spinner :processor="{ loading: loading }" />

		<div class="vendor-details__content" v-if="!loading">
			<div class="vendor-details__header">
				<div class="vendor-details__header-primary">
					<div class="vendor-details__header-status">
						<NotificationIcon
							:variant="notificationVariantIcon"
							@mouseenter="tooltipStatusVisible = true"
							@focus="tooltipStatusVisible = true"
							@mouseleave="tooltipStatusVisible = false"
							@blur="tooltipStatusVisible = false"
							ref="tooltipStatusRef"
						/>
						<Tooltip
							:text="vendorStatus"
							:trackedRefName="'tooltipStatusRef'"
							:display="tooltipStatusVisible"
							:offset="[4, 4]"
							position="top-start"
						/>
					</div>

					<div class="vendor-details__header-title-group">
						<span class="vendor-details__header-name">
							{{ vendorData.integratorName }}
						</span>
						<span
							class="vendor-details__header-alias"
							v-if="vendorData.alias"
						>
							{{ vendorData.alias || '' }}
						</span>

						<span class="vendor-details__header-int-code">
							{{ vendorData.integratorCode }}
						</span>
					</div>

					<div
						class="vendor-details__header-int-solution"
						v-if="hasIntegrations"
					>
						<TagLabel
							v-if="vendorData.dependentsCount"
							message="DMS Integration Solution"
							:subMessage="vendorData.dependentsCount"
							:variant="'hyperlink'"
							@click="showDependentModal = true"
							data-testid="withDependents"
						/>
						<TagLabel
							v-if="vendorData.useAggregatorIntegrations"
							message="USES DMS-IS"
							:state="'secondary-2'"
							data-testid="displayIfDMSIS"
						/>
						<TagLabel
							v-if="vendorData.useDirectIntegrations"
							message="DIRECT"
							:state="'secondary-1'"
							data-testid="displayIfDirect"
						/>
					</div>
				</div>
				<div class="vendor-details__header-secondary">
					<Toggle
						class="vendor-details__header-toggle"
						:label="
							vendorData.active
								? 'Active Partner'
								: 'Inactive Partner'
						"
						:disabled="disabledActiveDeactivate"
						:value="vendorData.active"
						@change="changeActiveDeactivate"
						v-if="showDeactivateToggle"
					/>
					<span class="vendor-details__header-spacer"></span>
					<div class="vendor-details__header-date">
						<div class="vendor-details__header-date-since">
							<span class="vendor-details__header-date-text">
								Partner since
							</span>

							<span
								class="vendor-details__header-date-text-emphasis"
							>
								{{ shortDateString(vendorData.dateActive) }}
							</span>
						</div>

						<div
							class="vendor-details__header-date-end"
							v-if="vendorData.dateInactive"
						>
							<span class="vendor-details__header-date-text">
								{{
									vendorData.active
										? 'Reactivated'
										: 'Deactivated'
								}}
							</span>

							<span
								class="vendor-details__header-date-text-emphasis"
							>
								{{ shortDateString(vendorData.dateInactive) }}
							</span>
						</div>
					</div>
				</div>
			</div>
			<div class="vendor-details__main-content">
				<SectionContainer class="vendor-details__main-content-details">
					<template #left>
						<span class="vendor-details__main-content-title"
							>Vendor Details</span
						>
					</template>
					<template #right>
						<Button
							className="btn btn--has-icon btn--small"
							iconClassName="icon-edit"
							labelText="Edit Details"
							:btnClick="editDetails"
							:iconBeforeLabel="true"
							v-if="hasUpdatePermission"
						/>
					</template>
					<template #content>
						<div class="vendor-details__main-content-details-info">
							<div
								class="vendor-details__main-content-details-left"
							>
								<ReadonlyField
									label="Vendor Name"
									:text="vendorData.integratorName"
								/>
								<ReadonlyField
									v-if="vendorData.alias"
									label="Alias Name"
									:text="vendorData.alias"
								/>
								<ReadonlyField
									label="Partner Since"
									:text="
										shortDateString(vendorData.dateActive)
									"
								/>
								<ReadonlyField
									label="DMS-IS"
									:text="vendorData.dmsis ? 'Yes' : 'No'"
								/>
								<ReadonlyField
									v-if="vendorData.website"
									label="Website"
								>
									<a class="link" :href="vendorData.website">
										{{ vendorData.website }}
									</a>
								</ReadonlyField>
								<ReadonlyField
									v-if="vendorData.confluenceLink"
									label="Confluence"
								>
									<a
										class="link"
										:href="vendorData.confluenceLink"
									>
										View Confluence page
									</a>
								</ReadonlyField>
								<ReadonlyField
									v-if="vendorData.address1"
									label="Address"
									:text="concatAddress"
								/>
							</div>
							<div
								class="vendor-details__main-content-details-right"
							>
								<ReadonlyField
									v-if="vendorData.internalNotes"
									label="Internal Notes"
									:text="vendorData.internalNotes"
								/>
								<ReadonlyField
									v-if="vendorData.description"
									label="3P Vendor Description"
									:text="vendorData.description"
								/>
							</div>
						</div>
					</template>
				</SectionContainer>
				<div class="vendor-details__main-content-group">
					<SectionContainer
						class="vendor-details__main-content-contacts"
					>
						<template #left>
							<span class="vendor-details__main-content-title"
								>Support Contact</span
							>
						</template>
						<template #right>
							<Button
								className="btn btn--small"
								name="contact"
								labelText="Contacts"
								:btnClick="handleContactButton"
							/>
						</template>
						<template #content>
							<ReadonlyField
								label="Contact Name"
								:text="contactName"
								v-if="!!contactName"
							/>
							<ReadonlyField
								label="Phone Number"
								:text="handlingPhoneValue"
								v-if="!!contactPhone"
							/>
							<ReadonlyField label="Email" v-if="!!contactEmail">
								<a
									class="link"
									:href="`mailto:${contactEmail}`"
								>
									{{ contactEmail }}
								</a>
							</ReadonlyField>
						</template>
					</SectionContainer>
					<SectionContainer
						class="vendor-details__main-content-troubleshoot"
					>
						<template #left>
							<span class="vendor-details__main-content-title"
								>Troubleshooting</span
							>
						</template>
						<template #content>
							<InlineEditing
								v-model="vuEniversityLink"
								class="vendor-details__header-date-text"
								labelText="VUEniversity URL"
								linkText="Info Guide"
								variant="hyperlink"
								data-test="inlineEditing"
								:apiCallFn="saveUrlVueniversity"
								@change="handleChange"
								@save="handleSave"
							>
							</InlineEditing>
						</template>
					</SectionContainer>
				</div>
			</div>
		</div>

		<VendorContactsFlyout v-model="showContactFlyout" :vendorId="id" />
		<VendorDetailsFlyout v-model="showDetailsFlyout" />
		<DependentVendorsModal
			v-model="showDependentModal"
			:integratorId="vendorId"
			:integratorName="vendorName"
		/>
		<CustomersModal v-model="showCustomerModal" />
	</div>
</template>

<script>
import ActionSetNav from '@/site-components/actions-set/action-set-nav';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import Button from '@/components/forms/button/button';
import SectionContainer from '@/components/shells/section-container/section-container';
import ReadonlyField from '@/components/forms/readonly-field/readonly-field';
import InlineEditing from '@/components/forms/inline-editing/inline-editing';
import Spinner from '@/components/visual/spinner/spinner';
import TagLabel from '@/components/visual/tag-label';
import Toggle from '@/components/forms/selection-control/toggle/toggle';
import VendorContactsFlyout from './vendor-contact-flyout';
import VendorDetailsFlyout from './vendor-details-flyout';
import DependentVendorsModal from '@/sections/vendors/dependentModal/dependent-vendors-modal';
import CustomersModal from '@/sections/shared/customers-modal';
import NotificationIcon from '@/components/notifications/notification-icon/notification-icon';
import Tooltip from '@/components/shells/tooltip/tooltip';
import { hasPermissionByGroupName } from '@/utils/function';

// mixins
import VendorsActions from '../summary/vendors-actions';
import DatetimeMixin from '@/mixins/datetime-mixin';

// vuex
import { mapActions, mapGetters } from 'vuex';
import {
	MODULE_NAME,
	SET_VENDOR,
	SET_VENDOR_BY_CODE,
	VENDOR,
	DEACTIVATE_VENDOR,
	SAVE_VUENIVERSITY
} from '../vendors-mutation-types';

import {
	MODULE_NAME as OPTIONS_MODULE_NAME,
	VENDORS as OPTIONS_VENDOR,
	STATES,
	SET_STATES
} from '@/sections/options/options-mutation-type';

import _ from 'lodash';
import pageKeyNames from '@/utils/page-key-names';

export default {
	name: 'VendorSettings',
	mixins: [DatetimeMixin, VendorsActions],
	components: {
		ActionSetNav,
		Breadcrumbs,
		Button,
		SectionContainer,
		ReadonlyField,
		InlineEditing,
		Spinner,
		TagLabel,
		Toggle,
		VendorContactsFlyout,
		VendorDetailsFlyout,
		DependentVendorsModal,
		CustomersModal,
		NotificationIcon,
		Tooltip
	},
	props: {
		id: {
			type: [Number, String]
		},
		integratorCode: {
			type: String
		},
		hasIntegrations: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			showContactFlyout: false,
			showDetailsFlyout: false,
			showDependentModal: false,
			showCustomerModal: false,
			vendorData: {},
			showConfirmationSaveModal: false,
			displayPopover: false,
			saveModalButtons: [
				{
					className: 'btn--primary',
					labelText: 'Yes, save changes',
					btnClick: this.save
				},
				{
					className: 'btn--link',
					labelText: 'Cancel',
					btnClick: this.cancelSaveVendor
				}
			],
			loading: false,
			displayFooter: false,
			tooltipStatusVisible: false,
			vuEniversityLink: '',
			vuEniversityLinkData: '',
			urlNames: [
				{
					url: `/vendors`,
					name: '3P Vendors'
				},
				{ name: '3P Vendor Details' }
			]
		};
	},
	computed: {
		...mapGetters(MODULE_NAME, [VENDOR]),
		...mapGetters(OPTIONS_MODULE_NAME, [OPTIONS_VENDOR, STATES]),
		vendorName() {
			return _.get(this, 'vendorData.integratorName', '');
		},
		vendorId() {
			return _.get(this, 'vendorData.id', 0);
		},
		contactName() {
			return _.get(this.vendorData, 'contact.name', '');
		},
		contactPhone() {
			return _.get(this.vendorData, 'contact.phone', '');
		},
		handlingPhoneValue() {
			let phoneNumber = this.contactPhone;
			if (!phoneNumber) {
				return '';
			}
			if (phoneNumber.length === 10) {
				return (
					'(' +
					phoneNumber.substring(0, 3) +
					')-' +
					phoneNumber.substring(3, 6) +
					'-' +
					phoneNumber.substring(6, phoneNumber.length)
				);
			} else {
				return (
					'+' +
					phoneNumber.substring(0, 1) +
					'(' +
					phoneNumber.substring(1, 4) +
					')-' +
					phoneNumber.substring(4, 7) +
					'-' +
					phoneNumber.substring(7, phoneNumber.length)
				);
			}
		},
		contactEmail() {
			return _.get(this.vendorData, 'contact.email', '');
		},
		hasUpdatePermission() {
			return (
				this.validatePermission('vendors.update') &&
				this.vendorData.active !== null
			);
		},
		disabledActiveDeactivate() {
			return this.vendorData.dmsis;
		},
		isIntegrationSolution() {
			return _.get(this, 'vendorData.dmsis', false);
		},
		hasVendorData() {
			return _.isEmpty(this.vendorData);
		},
		vendorStatus() {
			if (this.vendorData.active === null) {
				return 'Transaction Error';
			}
			if (!this.vendorData.active) {
				return 'Inactive Vendor';
			}
			return 'Active Vendor';
		},
		notificationVariantIcon() {
			if (this.vendorData.active === null) {
				return 'not-allowed';
			}
			if (!this.vendorData.active) {
				return 'inactive';
			}
			return 'positive';
		},
		showDeactivateToggle() {
			//hide the toggle
			return false;
			// if we have to handle the toggle remove the component below
			//return this.hasUpdatePermission && this.vendorData.active !== null;
		},
		confluenceLinkAttrs() {
			if (this.vendorData.confluenceLink) {
				return {
					href: this.vendorData.confluenceLink,
					target: '_blank'
				};
			}
			return {};
		},
		handlingPasswordValue() {
			if (this.hasOnlyReadPermission) {
				return '***************************';
			}
			const { password } = this.vendorData || '';
			return password;
		},
		hasOnlyReadPermission() {
			const user = this.$store.getters['oidcStore/oidcUser'];
			return hasPermissionByGroupName('vendors', 'read', user);
		},
		isMobile() {
			return this.screenSize.mobile;
		},
		variation() {
			if (this.isMobile) return 'top';
			return 'left';
		},
		maxLengthOnPasswordValue() {
			if (this.isMobile) return 10;
			return null;
		},
		concatAddress() {
			let state = null;
			const findStateByValue = (array, val) => {
				return array.find(objeto => objeto.value == val);
			};

			if (this[STATES].length > 0) {
				const stateObj = findStateByValue(
					this[STATES],
					this.vendorData.state
				);
				if (!_.isEmpty(stateObj)) {
					state = stateObj.text;
				}
			}

			return `${this.vendorData.address1}
				${this.vendorData.address2}, ${this.vendorData.city}, ${state} ${this.vendorData.zip}`;
		}
	},
	async created() {
		if (!this.id && !this.integratorCode) {
			this.setToastMessage({
				statusCode: 400,
				message: `Integrator id or code are required.`
			});
		}

		const parseResponse = () => {};

		if (this.id) {
			await this.getVendorById().then(() => {
				parseResponse();
			});
		}

		if (this.integratorCode) {
			await this.getVendorByCode().then(() => {
				parseResponse();
			});
		}
	},
	mounted() {
		this.$store.commit('setCurrentPage', pageKeyNames.VENDOR_SETTINGS);
	},
	destroyed() {
		this.$store.commit('setCurrentPage', null);
	},
	methods: {
		...mapActions(MODULE_NAME, {
			getVendorAction: SET_VENDOR,
			getVendorActionByCode: SET_VENDOR_BY_CODE,
			deactivateActivateVendorAction: DEACTIVATE_VENDOR,
			saveUrlVueniversityAction: SAVE_VUENIVERSITY
		}),
		...mapActions(OPTIONS_MODULE_NAME, [SET_STATES]),
		...mapActions('toastMessages', { setToastMessage: 'push' }),

		async getVendorById() {
			this.loading = true;
			await this.getVendorAction(this.id).then(hasResultValue => {
				if (!hasResultValue) {
					this.redirectToSummaryPage();
				}
			});
			this.loading = false;
		},
		async getVendorByCode() {
			this.loading = true;
			await this.getVendorActionByCode(this.integratorCode).then(
				hasResultValue => {
					if (!hasResultValue) {
						this.redirectToSummaryPage();
					}
				}
			);
			this.loading = false;
		},
		handleContactButton() {
			this.showContactFlyout = !this.showContactFlyout;
		},
		editDetails() {
			if (this.hasUpdatePermission) {
				this.showDetailsFlyout = true;
			}
		},
		redirectToSummaryPage() {
			this.$router.push({
				path: '/vendors'
			});
		},
		cancelSaveVendor() {
			this.showConfirmationSaveModal = false;
		},
		async deactivateActivateVendor() {
			const self = this;
			const id = self.id;
			let isActive = self.vendorData.active;
			const action = isActive ? 'activate' : 'deactivate';
			await self
				.deactivateActivateVendorAction({ id, action })
				.then(resp => {
					if (!resp) {
						self.vendorData.active = !isActive;
					}
				});
		},
		cancelInactiveVendor() {
			this.vendorData.active = !this.vendorData.active;
		},
		changeActiveDeactivate() {
			this.vendorData.active = !this.vendorData.active;
		},
		popoverClass() {
			this.displayPopover = !this.displayPopover;
		},
		closePopoverHandler() {
			this.displayPopover = false;
		},
		async saveUrlVueniversity() {
			return this.saveUrlVueniversityAction({
				id: this.vendorData.id,
				VUEniversityLink: this.vuEniversityLinkData
			});
		},
		handleChange(val) {
			this.vuEniversityLinkData = val;
		},
		handleSave(val) {
			this.vuEniversityLink = val;
		}
	},
	watch: {
		[STATES]: {
			immediate: true,
			deep: true,
			async handler(val) {
				if (val.length == 0) {
					await this[SET_STATES]();
				}
			}
		},
		[VENDOR]: {
			deep: true,
			immediate: true,
			handler(obj) {
				this.vendorData = _.cloneDeep(obj);
				if (!_.isEmpty(obj)) {
					this.vuEniversityLink = obj.vuEniversityLink;
					this.vuEniversityLinkData = obj.vuEniversityLink;
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
  * 3P Vendor Details
  * Can use SCSS variables = TRUE
**/

.vendor-details {
	&__content {
		display: flex;
		flex-direction: column;
		background: $white;
		padding: $padding-lg $padding-lg $padding-lg $padding-md;
		border: 1px solid $neutral-3;
		border-radius: $form-border-radius;
	}

	&__header {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;

		&-primary {
			display: grid;
			grid-template-columns: 1fr auto;
			column-gap: $gutter-md;
		}		

		&-title-group {
			display: flex;
			flex-direction: column;
			flex-wrap: wrap;
			gap: $gutter-md;
		}

		&-name {
			@include heading('md');
			@include ellipsis;
			max-width: 260px;

			@include mq($from: md-l) {
				max-width: 360px;
			}
		}

		&-alias,
		&-int-code {
			@include body-text('sm-supporting');
			text-transform: uppercase;
		}

		&-int-solution {
			display: flex;
			flex-wrap: wrap;
			gap: $gutter-md;
			grid-column: 2 / 3;
			margin-top: $gutter-lg;
		}

		&-secondary {
			display: flex;
			align-items: center;
			gap: $gutter-xl;
		}

		&-spacer {
			height: 70px;
			width: 1px;
			background: $neutral-3;
			height: 36px;
		}

		&-date {
			&-since,
			&-end {
				width: 180px;
				display: flex;
				justify-content: space-between;
			}

			&-end {
				margin-top: $gutter-md;
			}

			&-text {
				@include body-text('regular');
				color: $neutral-2;
			}

			&-text-emphasis {
				@include body-text('regular');
			}
		}
	}

	&__main-content {
		display: grid;
		grid-template-columns: 1fr 1fr;		
		margin-top: $gutter-xl;
		column-gap: $gutter-xl * 2;

		@include mq($from: md-l) {
			grid-template-columns: 2fr 1fr;
		}

	}

	&__main-content-title {
		@include body-text('regular-emphasis');
	}

	&__main-content-details-info {
		display: grid;
		grid-template-columns: 1fr;
		column-gap: $gutter-xl;

		@include mq($from: md-l) {
			grid-template-columns: 1fr 1fr;
		}
	}

	&__main-content-details-right {
		::v-deep .readonly-field {
			grid-template-columns: 1fr;
			row-gap: $gutter-lg;

			&:not(:last-child) {
				margin-bottom: $gutter-xl;
			}
		}

		@include mq($until: md-l) {
			margin-top: $gutter-xl;
		}
	}

	&__main-content-group {
		display: flex;
		flex-direction: column;
		gap: $gutter-xxl;
	}
}
</style>
